import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx-js-style';
import { Box, Typography, TextField, IconButton, Menu, MenuItem, Portal, Chip, Checkbox, Button, CircularProgress, } from '@mui/material';
import { RefreshCw, Download, BookOpen, TrendingDown, Inbox, Radio, User, UserCheck, ExternalLink, Send, } from 'lucide-react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ApiCall } from '../../helper/axios';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSocialYtTrackerDetails, manageSocialYtTracker, clearSocialYtCurrentDetails, } from '../../redux/socialYtTrackerSlice';
import { selectEntityType } from '../../redux/authSlice';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import { useLayout } from '../../contexts/LayoutContext';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import PageContainer from '../../common_components/PageContainer';
import useToast from '../../common_components/Toast/useToast';
import { ROUTES } from '../../routes/routes.constants';
import NotesModal from '../../common_components/NotesModal';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import MetricCard from '../../common_components/MetricCard';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import { Dropdown, ExportButton } from '../../common_components/FormInputs';
import { FloatingSaveBar } from '../../common_components/FloatingSaveBar';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import AssignToListModal from '../PublisherTracker/components/AssignToListModal';
// ─── Tier pill color map ────────────────────────────────────────────────────
const tierStyles = {
    Nano: { bg: '#F2F4F7', color: '#344054' },
    Micro: { bg: '#EFF8FF', color: '#026AA2' },
    'Mid-Tier': { bg: '#F4F3FF', color: '#5925DC' },
    Macro: { bg: '#FFF4ED', color: '#C4320A' },
    Mega: { bg: '#FDF2FA', color: '#C11574' },
};
// ─── Status chip styles ──────────────────────────────────────────────────────
const statusStyles = {
    Pending: { bg: '#FFF4ED', color: '#C2410C', label: 'Pending' },
    'In-Progress': { bg: '#EFF8FF', color: '#026AA2', label: 'In Progress' },
    Approved: { bg: '#ECFDF3', color: '#027A48', label: 'Approved' },
    Rejected: { bg: '#FEF3F2', color: '#B42318', label: 'Rejected' },
};
// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtNumber = (n) => n == null ? '—' : n.toLocaleString();
export default function AddSocialYtTracker({ viewOnly = false } = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { id: trackIdFromParam } = useParams();
    const queryTrackerId = searchParams.get('id') || searchParams.get('trackerId') || '';
    const trackerId = trackIdFromParam || queryTrackerId || location.state?.trackerId;
    const dispatch = useDispatch();
    const toast = useToast();
    const { setIsSidebarExpanded } = useLayout();
    const { selectedClientId } = useAccountOverview();
    const reduxClientName = useSelector((state) => state.accountOverview.clientName);
    const token = useSelector((state) => state.auth.token);
    const entityType = useSelector(selectEntityType);
    const isClient = entityType === 'client';
    const MODULE_KEY = 'affiliate_account_performance';
    const permissions = useSelector((state) => state.auth.permissions);
    const affiliateActions = permissions?.[MODULE_KEY]?.actions || [];
    const hasManageTrackersAccess = affiliateActions.includes('Manage Trackers');
    const hasClientApprovalAccess = affiliateActions.includes('Client Approval Access');
    // Derive tracker type from navigation state or search params or from contacts (set after fetch)
    const typeFromQuery = (searchParams.get('type') || searchParams.get('trackerType'));
    const [trackerType, setTrackerType] = useState(location.state?.trackerType || typeFromQuery || 'social');
    // ── Redux state ──────────────────────────────────────────────────────────
    const { currentListDetails = [], loading } = useSelector((state) => state.socialYtTracker);
    // ── Tracker name inline edit ─────────────────────────────────────────────
    const [trackerListName, setTrackerListName] = useState(location.state?.trackerListName || searchParams.get('name') || '');
    const displayClientName = useMemo(() => {
        const fromState = location.state?.clientName;
        if (fromState)
            return fromState;
        const fromFirstRow = currentListDetails[0]?.client_id?.name;
        if (fromFirstRow)
            return fromFirstRow;
        if (reduxClientName)
            return reduxClientName;
        // Fallback: extract client name from tracker list name if pattern matches "Client Name ..."
        if (trackerListName) {
            const match = trackerListName.match(/^(.*?)\s+(Social|YouTube|Outreach)\s+Tracker/i);
            if (match && match[1] && match[1].toLowerCase() !== 'general' && match[1].toLowerCase() !== 'client') {
                return match[1].trim();
            }
        }
        return '';
    }, [location.state, currentListDetails, reduxClientName, trackerListName]);
    const [editingTrackerName, setEditingTrackerName] = useState(false);
    const [tempTrackerName, setTempTrackerName] = useState('');
    const [savingTrackerName, setSavingTrackerName] = useState(false);
    // ── Table filter / pagination state ─────────────────────────────────────
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('All');
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;
    // ── Row selection ─────────────────────────────────────────────────────────
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    // ── Pending inline edits (staged, saved via FloatingSaveBar) ─────────────
    const [pendingEdits, setPendingEdits] = useState({});
    // ── Bulk actions state ────────────────────────────────────────────────────
    const [stagedStatus, setStagedStatus] = useState('');
    const [savingPending, setSavingPending] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    // ── Notes dialog ──────────────────────────────────────────────────────────
    const [notesOpen, setNotesOpen] = useState(false);
    const [selectedNotesRow, setSelectedNotesRow] = useState(null);
    const [notesValue, setNotesValue] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    // ── HubSpot menu ──────────────────────────────────────────────────────────
    const [hubspotAnchor, setHubspotAnchor] = useState(null);
    const [assignToListOpen, setAssignToListOpen] = useState(false);
    // ── Inline editing ────────────────────────────────────────────────────────
    const [editingCell, setEditingCell] = useState(null);
    const [tempCellValue, setTempCellValue] = useState('');
    // ── On mount ──────────────────────────────────────────────────────────────
    useEffect(() => {
        setIsSidebarExpanded(false);
        dispatch(setActiveModuleKey(MODULE_KEY));
        return () => { dispatch(setActiveModuleKey(null)); };
    }, [setIsSidebarExpanded]);
    // Sync trackerId to URL search params so page refresh preserves tracker data
    useEffect(() => {
        if (trackerId && searchParams.get('id') !== trackerId) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('id', trackerId);
                return next;
            }, { replace: true });
        }
    }, [trackerId, searchParams, setSearchParams]);
    useEffect(() => {
        if (trackerId) {
            dispatch(fetchSocialYtTrackerDetails({ id: trackerId }));
        }
        else {
            dispatch(clearSocialYtCurrentDetails());
        }
    }, [dispatch, trackerId]);
    // ── Sync tracker name & type from fetched contacts ────────────────────────
    useEffect(() => {
        if (currentListDetails.length > 0) {
            const first = currentListDetails[0];
            if (first.name && !trackerListName) {
                setTrackerListName(first.name);
            }
            if (first.tracker_type) {
                setTrackerType(first.tracker_type);
            }
        }
    }, [currentListDetails]);
    // ── Column labels based on tracker type ──────────────────────────────────
    const followersLabel = trackerType === 'youtube' ? 'Subscribers' : 'Followers';
    const engagementLabel = trackerType === 'youtube' ? 'Monthly Visits' : 'Engagement %';
    // ── Filtered rows (no multicontact child rows) ────────────────────────────
    const filteredRows = useMemo(() => {
        return currentListDetails.filter(row => {
            if (selectedStatusFilter !== 'All' && row.status !== selectedStatusFilter)
                return false;
            return true;
        });
    }, [currentListDetails, selectedStatusFilter]);
    const totalEntries = filteredRows.length;
    const totalPages = Math.ceil(totalEntries / itemsPerPage);
    const paginatedRows = useMemo(() => {
        const start = (page - 1) * itemsPerPage;
        return filteredRows.slice(start, start + itemsPerPage);
    }, [filteredRows, page]);
    // ── Metric counts ─────────────────────────────────────────────────────────
    const totalContactsCount = currentListDetails.length;
    const bouncedContacts = useMemo(() => currentListDetails.filter(c => !!c.bounce_reason), [currentListDetails]);
    const ineligibleContacts = useMemo(() => currentListDetails.filter(c => c.sequences_is_enrolled === 'true'), [currentListDetails]);
    const eligibleContacts = useMemo(() => currentListDetails.filter(c => c.sequences_is_enrolled !== 'true'), [currentListDetails]);
    const newContacts = useMemo(() => currentListDetails.filter(c => !c.hubspot_sync_status || c.hubspot_sync_status === 'null'), [currentListDetails]);
    // ── Latest HubSpot sync date ──────────────────────────────────────────────
    const latestHubspotSyncDate = useMemo(() => {
        const dates = currentListDetails
            .map(c => c.hubspot_sync_date)
            .filter(Boolean)
            .map(d => new Date(d).getTime())
            .filter(t => !isNaN(t));
        if (dates.length === 0)
            return null;
        return new Date(Math.max(...dates)).toISOString();
    }, [currentListDetails]);
    // ── Export XLSX ───────────────────────────────────────────────────────────
    const exportContactsToXLSX = (contacts, filename) => {
        if (!contacts || contacts.length === 0) {
            toast.warning('No contacts available to export.');
            return;
        }
        const isLimitedClient = isClient && !hasManageTrackersAccess;
        const headers = isLimitedClient ? [
            'Creator',
            'Page Link',
            followersLabel,
            engagementLabel,
            'Tier',
            'US Based',
            'Gender',
            'Status',
        ] : [
            'Creator',
            'Page Link',
            'First Name',
            'Last Name',
            'Primary Email',
            'Secondary Email',
            followersLabel,
            engagementLabel,
            'Tier',
            'US Based',
            'Gender',
            'Status',
            'HubSpot Sync Status',
            'Bounce Reason',
        ];
        const headerRow = headers.map(h => ({
            v: h,
            t: 's',
            s: { font: { bold: true, color: { rgb: '000000' } }, fill: { fgColor: { rgb: 'D9D9D9' } } },
        }));
        const dataRows = contacts.map(c => {
            if (isLimitedClient) {
                return [
                    c.creator || '',
                    c.page_link || '',
                    trackerType === 'youtube'
                        ? (c.subscribers ?? '')
                        : (c.followers ?? ''),
                    trackerType === 'youtube'
                        ? (c.monthly_visits ?? '')
                        : (c.engagement_percent ?? ''),
                    c.tier || '',
                    c.us_based ? 'Yes' : 'No',
                    c.gender || '',
                    c.status || '',
                ];
            }
            return [
                c.creator || '',
                c.page_link || '',
                c.first_name || '',
                c.last_name || '',
                c.primary_email || '',
                c.secondary_email || '',
                trackerType === 'youtube'
                    ? (c.subscribers ?? '')
                    : (c.followers ?? ''),
                trackerType === 'youtube'
                    ? (c.monthly_visits ?? '')
                    : (c.engagement_percent ?? ''),
                c.tier || '',
                c.us_based ? 'Yes' : 'No',
                c.gender || '',
                c.status || '',
                c.hubspot_sync_status || '',
                c.bounce_reason || '',
            ];
        });
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
        ws['!cols'] = headers.map((_, i) => ({
            wch: Math.max(headers[i].length, ...dataRows.map(r => String(r[i] ?? '').length)) + 4,
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
        XLSX.writeFile(wb, `${filename}.xlsx`);
        toast.success('Spreadsheet exported successfully.');
    };
    // ── Refresh ───────────────────────────────────────────────────────────────
    const refreshTrackerData = () => {
        if (trackerId) {
            dispatch(fetchSocialYtTrackerDetails({ id: trackerId }));
        }
    };
    const getPendingValue = (row, field) => {
        return pendingEdits[row.id]?.[field] ?? row[field];
    };
    const getPendingValueString = (row, field) => {
        const value = getPendingValue(row, field);
        return value == null ? '' : String(value);
    };
    // ── Tracker name save ─────────────────────────────────────────────────────
    const handleSaveTrackerName = async () => {
        const trimmed = tempTrackerName.trim();
        if (!trimmed || trimmed === trackerListName) {
            setEditingTrackerName(false);
            return;
        }
        if (!trackerId) {
            setTrackerListName(trimmed);
            setEditingTrackerName(false);
            return;
        }
        setSavingTrackerName(true);
        try {
            await dispatch(manageSocialYtTracker({
                action: 'update_tracker',
                tracker_id: trackerId,
                updates: { name: trimmed },
            })).unwrap();
            setTrackerListName(trimmed);
            setEditingTrackerName(false);
            toast.success('Tracker name updated successfully!');
        }
        catch (err) {
            toast.error(err || 'Failed to update tracker name.');
        }
        finally {
            setSavingTrackerName(false);
        }
    };
    const handleCancelTrackerName = () => {
        setTempTrackerName(trackerListName);
        setEditingTrackerName(false);
    };
    // ── Notes ─────────────────────────────────────────────────────────────────
    const handleOpenNotes = (row) => {
        setSelectedNotesRow(row);
        setNotesValue(row.notes || '');
        setNotesOpen(true);
    };
    const handleSaveNotes = async (newNotes) => {
        if (!selectedNotesRow)
            return;
        setNotesSaving(true);
        try {
            await dispatch(manageSocialYtTracker({
                action: 'update_contact',
                contact_id: selectedNotesRow.id,
                updates: { notes: newNotes.trim() },
            })).unwrap();
            toast.success('Notes updated successfully!');
            setNotesOpen(false);
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to update notes.');
            throw err;
        }
        finally {
            setNotesSaving(false);
        }
    };
    // ── Stage inline edit (does NOT save to DB — FloatingSaveBar saves all) ──
    const handleInlineSave = (row, field, value) => {
        setEditingCell(null);
        const trimmed = value.trim();
        const realField = field === 'followers_or_subs'
            ? (trackerType === 'youtube' ? 'subscribers' : 'followers')
            : field;
        const currentVal = String(row[realField] ?? '');
        if (currentVal === trimmed) {
            // No change — remove from pending if it was there
            setPendingEdits(prev => {
                const rowEdits = { ...(prev[row.id] || {}) };
                delete rowEdits[realField];
                if (Object.keys(rowEdits).length === 0) {
                    const next = { ...prev };
                    delete next[row.id];
                    return next;
                }
                return { ...prev, [row.id]: rowEdits };
            });
            return;
        }
        const storeValue = field === 'followers_or_subs'
            ? (trimmed === '' ? null : Number(trimmed.replace(/,/g, '')))
            : trimmed;
        setPendingEdits(prev => ({
            ...prev,
            [row.id]: { ...(prev[row.id] || {}), [realField]: storeValue },
        }));
    };
    // ── Direct US Based toggle ─────────────────────────────────────────────────
    const handleUsBasedToggle = async (row) => {
        if (isClient || viewOnly)
            return;
        const newVal = !row.us_based;
        try {
            await dispatch(manageSocialYtTracker({
                action: 'update_contact',
                contact_id: row.id,
                updates: { us_based: newVal },
            })).unwrap();
            toast.success(`Updated US Based for ${row.creator || 'contact'}.`);
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to update US Based.');
        }
    };
    // ── Send For Approval ─────────────────────────────────────────────────────
    const [sendingApproval, setSendingApproval] = useState(false);
    const handleSendForApproval = async () => {
        if (!trackerId)
            return;
        setSendingApproval(true);
        try {
            const payload = { tracker_id: trackerId };
            if (selectedRowIds.size > 0) {
                payload.contact_ids = Array.from(selectedRowIds);
            }
            const res = await ApiCall('POST', '/api/social-yt-tracker/send-for-approval', payload, { Authorization: token, 'x-module-key': 'affiliate_account_performance' });
            const data = res?.data;
            if (data?.success) {
                toast.success(data.message || 'Successfully sent to client for approval!');
                setSelectedRowIds(new Set());
                refreshTrackerData();
            }
            else {
                toast.error(data?.message || 'Failed to send for approval.');
            }
        }
        catch (err) {
            toast.error(err?.message || 'Failed to send for approval.');
        }
        finally {
            setSendingApproval(false);
        }
    };
    // Check if active editing cell has changes compared to original row
    const hasActiveCellChange = useMemo(() => {
        if (!editingCell)
            return false;
        const activeRow = currentListDetails.find(r => r.id === editingCell.rowId);
        if (!activeRow)
            return false;
        const realField = editingCell.field === 'followers_or_subs'
            ? (trackerType === 'youtube' ? 'subscribers' : 'followers')
            : editingCell.field;
        return String(activeRow[realField] ?? '') !== tempCellValue.trim();
    }, [editingCell, tempCellValue, currentListDetails, trackerType]);
    const pendingEditRowsCount = useMemo(() => {
        const rowKeys = new Set(Object.keys(pendingEdits));
        if (hasActiveCellChange && editingCell) {
            rowKeys.add(editingCell.rowId);
        }
        return rowKeys.size;
    }, [pendingEdits, hasActiveCellChange, editingCell]);
    // ── Save all staged edits ─────────────────────────────────────────────────
    const handleSaveAllPendingEdits = async () => {
        setSavingPending(true);
        try {
            let currentEdits = { ...pendingEdits };
            if (editingCell) {
                const activeRow = currentListDetails.find(r => r.id === editingCell.rowId);
                const trimmed = tempCellValue.trim();
                const realField = editingCell.field === 'followers_or_subs'
                    ? (trackerType === 'youtube' ? 'subscribers' : 'followers')
                    : editingCell.field;
                if (activeRow && String(activeRow[realField] ?? '') !== trimmed) {
                    const storeValue = editingCell.field === 'followers_or_subs'
                        ? (trimmed === '' ? null : Number(trimmed.replace(/,/g, '')))
                        : trimmed;
                    currentEdits = {
                        ...currentEdits,
                        [editingCell.rowId]: {
                            ...(currentEdits[editingCell.rowId] || {}),
                            [realField]: storeValue
                        }
                    };
                }
                setEditingCell(null);
            }
            if (Object.keys(currentEdits).length === 0) {
                setSavingPending(false);
                return;
            }
            await Promise.all(Object.entries(currentEdits).map(([rowId, updates]) => dispatch(manageSocialYtTracker({
                action: 'update_contact',
                contact_id: rowId,
                updates,
            })).unwrap()));
            toast.success('Changes saved successfully!');
            setPendingEdits({});
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to save changes.');
        }
        finally {
            setSavingPending(false);
        }
    };
    // ── Bulk status change ────────────────────────────────────────────────────
    const handleBulkStatusChange = async (status) => {
        setSavingPending(true);
        try {
            const promises = Array.from(selectedRowIds).map(rowId => dispatch(manageSocialYtTracker({
                action: 'update_contact',
                contact_id: rowId,
                updates: { status },
            })).unwrap());
            await Promise.all(promises);
            toast.success(`Updated status to ${status} for ${selectedRowIds.size} contacts.`);
            setSelectedRowIds(new Set());
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err?.response?.data?.message || err || 'Failed to update status.');
        }
        finally {
            setSavingPending(false);
        }
    };
    // ── Bulk delete ───────────────────────────────────────────────────────────
    const handleBulkDelete = async () => {
        setSavingPending(true);
        try {
            await dispatch(manageSocialYtTracker({
                action: 'delete_contact',
                contact_ids: Array.from(selectedRowIds),
            })).unwrap();
            toast.success(`Deleted ${selectedRowIds.size} contacts successfully!`);
            setSelectedRowIds(new Set());
            setDeleteModalOpen(false);
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err?.response?.data?.message || err || 'Failed to delete selected contacts.');
        }
        finally {
            setSavingPending(false);
        }
    };
    const isBounced = (c) => Boolean(c?.bounce_reason && String(c.bounce_reason).trim() !== '');
    const isIneligible = (c) => c?.sequences_is_enrolled === 'true' ||
        c?.sequence_enrolled === 'true' ||
        isBounced(c);
    // Only Approved contacts may be pushed to HubSpot
    const isApproved = (c) => String(c?.status || '').trim().toLowerCase() === 'approved';
    // ── HubSpot contact sync (all or selected) ────────────────────────────────
    const handleContactSync = async () => {
        setHubspotAnchor(null);
        const ids = selectedRowIds.size > 0
            ? Array.from(selectedRowIds).filter(id => {
                const row = currentListDetails.find(r => r.id === id);
                return row?.primary_email?.trim() && isApproved(row);
            })
            : currentListDetails
                .filter(r => r.primary_email?.trim() && isApproved(r))
                .map(r => r.id);
        if (ids.length === 0) {
            const pool = selectedRowIds.size > 0
                ? Array.from(selectedRowIds).map(id => currentListDetails.find(r => r.id === id))
                : currentListDetails;
            const hasUnapproved = pool.some(r => r && !isApproved(r));
            toast.error(hasUnapproved
                ? 'Only Approved contacts can be synced to HubSpot. Approve the contacts first.'
                : 'No Approved contacts with an email found to sync.');
            return;
        }
        setSavingPending(true);
        try {
            const res = await ApiCall('POST', '/api/social-yt-tracker/hubspot-sync', { type: 'direct_sync', contact_ids: ids }, { Authorization: token, 'x-module-key': 'affiliate_account_performance' });
            const data = res?.data;
            if (data?.success) {
                const synced = data.data?.results?.filter((r) => r.status === 'Synced').length ?? 0;
                const failed = data.data?.results?.filter((r) => r.status === 'Failed').length ?? 0;
                toast.success(`HubSpot sync complete — ${synced} synced${failed ? `, ${failed} failed` : ''}.`);
            }
            else {
                toast.error(data?.message || 'HubSpot sync failed.');
            }
        }
        catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'HubSpot sync failed.');
        }
        finally {
            try {
                await refreshTrackerData();
            }
            catch { }
            setSavingPending(false);
        }
    };
    // ── HubSpot sync from FloatingSaveBar ─────────────────────────────────────
    const handleBulkHubspotSync = async () => {
        const selectedIds = [];
        selectedRowIds.forEach(id => {
            selectedIds.push(id);
            const children = currentListDetails.filter(c => c.parent_id === id);
            children.forEach(ch => {
                if (ch.primary_email?.trim() && !selectedIds.includes(ch.id)) {
                    selectedIds.push(ch.id);
                }
            });
        });
        const validIds = selectedIds.filter(id => {
            const row = currentListDetails.find(r => r.id === id);
            return row?.primary_email?.trim() && isApproved(row) && !isIneligible(row) && !isBounced(row);
        });
        if (validIds.length === 0) {
            const hasUnapproved = selectedIds.some(id => {
                const row = currentListDetails.find(r => r.id === id);
                return row && !isApproved(row);
            });
            toast.error(hasUnapproved
                ? 'Only Approved contacts can be synced to HubSpot. Approve the selected contacts first.'
                : 'No eligible contacts with valid email to sync.');
            return;
        }
        setSavingPending(true);
        try {
            const res = await ApiCall('POST', '/api/social-yt-tracker/hubspot-sync', { type: 'direct_sync', contact_ids: validIds }, { Authorization: token, 'x-module-key': 'affiliate_account_performance' });
            const data = res?.data;
            if (data?.success) {
                const synced = data.data?.results?.filter((r) => r.status === 'Synced').length ?? 0;
                const failed = data.data?.results?.filter((r) => r.status === 'Failed').length ?? 0;
                toast.success(`HubSpot sync complete — ${synced} synced${failed ? `, ${failed} failed` : ''}.`);
                setSelectedRowIds(new Set());
                refreshTrackerData();
            }
            else {
                toast.error(data?.message || 'HubSpot sync failed.');
            }
        }
        catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to sync to HubSpot.');
        }
        finally {
            setSavingPending(false);
        }
    };
    // ── Navigation ────────────────────────────────────────────────────────────
    const handleBack = () => {
        navigate(ROUTES.accountOverview.path, {
            state: { restoreTab: trackerType === 'youtube' ? 'youtube_tracker' : 'social_tracker' }
        });
    };
    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = [
        { id: 'creator', label: 'Creator', width: 140, align: 'left', sortable: true },
        { id: 'page_link', label: 'Page Link', width: 120, align: 'left', sortable: false },
        { id: 'first_name', label: 'First Name', width: 120, align: 'left', sortable: true },
        { id: 'last_name', label: 'Last Name', width: 120, align: 'left', sortable: true },
        { id: 'primary_email', label: 'Primary Email', width: 180, align: 'left', sortable: true },
        { id: 'followers_or_subs', label: followersLabel, width: 110, align: 'left', sortable: true },
        { id: 'engagement_or_visits', label: engagementLabel, width: 130, align: 'left', sortable: true },
        { id: 'tier', label: 'Tier', width: 90, align: 'left', sortable: true },
        { id: 'us_based', label: 'US Based', width: 80, align: 'center', sortable: false },
        { id: 'gender', label: 'Audience', width: 90, align: 'left', sortable: true },
        { id: 'status', label: 'Status', width: 110, align: 'left', sortable: true },
        { id: 'action', label: 'Action', width: 70, align: 'center', sortable: false },
    ];
    // ── Shared inline editable cell renderer ─────────────────────────────────
    const renderEditableCell = (row, field, displayValue, placeholder, inputType = 'text', fontWeight = 400) => {
        const currentValue = field === 'followers_or_subs'
            ? getPendingValueString(row, trackerType === 'youtube' ? 'subscribers' : 'followers')
            : getPendingValueString(row, field);
        if ((isClient && !hasManageTrackersAccess) || viewOnly) {
            return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: currentValue ? '#475467' : '#98A2B3', fontStyle: currentValue ? 'normal' : 'italic', fontWeight }, children: currentValue || placeholder }));
        }
        const isEditing = editingCell?.rowId === row.id && editingCell?.field === field;
        if (isEditing) {
            return (_jsx(TextField, { value: tempCellValue, onChange: e => setTempCellValue(e.target.value), onBlur: () => handleInlineSave(row, field, tempCellValue), onKeyDown: e => {
                    if (e.key === 'Enter')
                        handleInlineSave(row, field, tempCellValue);
                    if (e.key === 'Escape')
                        setEditingCell(null);
                }, autoFocus: true, size: "small", type: inputType, helperText: field === 'primary_email' && (row.hubspot_sync_date || row.hubspot_contact_id)
                    ? '⚠️ Changing email will reset sync data'
                    : undefined, FormHelperTextProps: {
                    sx: { fontSize: '10px !important', color: '#B54708 !important', m: '2px 0 0 0', lineHeight: 1.2 }
                }, sx: {
                    minWidth: inputType === 'number' ? '80px' : '110px',
                    '& .MuiOutlinedInput-root': {
                        height: '32px', fontSize: '13px',
                        '& fieldset': { borderColor: '#D0D5DD', borderRadius: '6px' },
                        '&:hover fieldset': { borderColor: '#EE2A7B' },
                        '&.Mui-focused fieldset': { borderColor: '#EE2A7B', boxShadow: '0px 0px 0px 4px rgba(238,42,123,0.1)' },
                    },
                } }));
        }
        return (_jsx(Typography, { className: "all-Font-Sizes", onClick: () => { setEditingCell({ rowId: row.id, field }); setTempCellValue(currentValue); }, sx: {
                fontSize: '14px !important',
                cursor: 'pointer', fontWeight,
                color: currentValue ? '#475467' : '#98A2B3',
                fontStyle: currentValue ? 'normal' : 'italic',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                '&:hover': { color: '#EE2A7B' },
            }, children: currentValue || placeholder }));
    };
    // ── Cell renderer ─────────────────────────────────────────────────────────
    const renderCell = (row, column) => {
        switch (column.id) {
            case 'creator':
                return renderEditableCell(row, 'creator', row.creator || '', 'Creator Name', 'text', 600);
            case 'page_link': {
                const pageLinkValue = pendingEdits[row.id]?.page_link ?? row.page_link;
                if ((isClient && !hasManageTrackersAccess) || viewOnly) {
                    return pageLinkValue ? (_jsx(HoverTooltip, { title: pageLinkValue, placement: "top", arrow: true, children: _jsx(IconButton, { size: "small", onClick: () => window.open(pageLinkValue, '_blank'), sx: { p: '4px', color: '#026AA2' }, children: _jsx(ExternalLink, { size: 15 }) }) })) : _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#98A2B3' }, children: "\u2014" });
                }
                const isEditing = editingCell?.rowId === row.id && editingCell?.field === 'page_link';
                if (isEditing) {
                    return (_jsx(TextField, { value: tempCellValue, onChange: e => setTempCellValue(e.target.value), onBlur: () => handleInlineSave(row, 'page_link', tempCellValue), onKeyDown: e => {
                            if (e.key === 'Enter')
                                handleInlineSave(row, 'page_link', tempCellValue);
                            if (e.key === 'Escape')
                                setEditingCell(null);
                        }, autoFocus: true, size: "small", sx: {
                            minWidth: '120px',
                            '& .MuiOutlinedInput-root': {
                                height: '32px', fontSize: '13px',
                                '& fieldset': { borderColor: '#D0D5DD', borderRadius: '6px' },
                                '&:hover fieldset': { borderColor: '#EE2A7B' },
                                '&.Mui-focused fieldset': { borderColor: '#EE2A7B', boxShadow: '0px 0px 0px 4px rgba(238,42,123,0.1)' },
                            },
                        } }));
                }
                return (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: pageLinkValue ? (_jsxs(_Fragment, { children: [_jsx(Typography, { className: "all-Font-Sizes", onClick: () => { setEditingCell({ rowId: row.id, field: 'page_link' }); setTempCellValue(pageLinkValue); }, sx: { fontSize: '14px !important', cursor: 'pointer', color: '#475467', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80px', '&:hover': { color: '#EE2A7B' } }, children: pageLinkValue }), _jsx(HoverTooltip, { title: pageLinkValue, placement: "top", arrow: true, children: _jsx(IconButton, { size: "small", onClick: () => window.open(pageLinkValue, '_blank'), sx: { p: '2px', color: '#026AA2', flexShrink: 0 }, children: _jsx(ExternalLink, { size: 13 }) }) })] })) : (_jsx(Typography, { className: "all-Font-Sizes", onClick: () => { setEditingCell({ rowId: row.id, field: 'page_link' }); setTempCellValue(''); }, sx: { fontSize: '14px !important', cursor: 'pointer', color: '#98A2B3', fontStyle: 'italic', '&:hover': { color: '#EE2A7B' } }, children: "Page Link" })) }));
            }
            case 'first_name':
                return renderEditableCell(row, 'first_name', row.first_name || '', 'First Name');
            case 'last_name':
                return renderEditableCell(row, 'last_name', row.last_name || '', 'Last Name');
            case 'primary_email':
                return renderEditableCell(row, 'primary_email', row.primary_email || '', 'Email Address');
            case 'followers_or_subs': {
                const val = trackerType === 'youtube' ? row.subscribers : row.followers;
                return renderEditableCell(row, 'followers_or_subs', val != null ? String(val) : '', followersLabel, 'number');
            }
            case 'engagement_or_visits':
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#475467' }, children: trackerType === 'youtube'
                        ? fmtNumber(row.monthly_visits)
                        : row.engagement_percent != null
                            ? `${row.engagement_percent}%`
                            : '—' }));
            case 'tier': {
                const ts = tierStyles[row.tier] || { bg: '#F2F4F7', color: '#344054' };
                return row.tier ? (_jsx(Chip, { label: row.tier, size: "small", sx: {
                        height: 22,
                        borderRadius: '999px',
                        bgcolor: ts.bg,
                        color: ts.color,
                        fontSize: '12px',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: '8px' },
                    } })) : (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#98A2B3' }, children: "\u2014" }));
            }
            case 'us_based': {
                const currentUsBased = !!row.us_based;
                const isUsBasedDisabled = isClient || viewOnly;
                return (_jsx(Checkbox, { checked: currentUsBased, disabled: isUsBasedDisabled, size: "small", onChange: () => !isUsBasedDisabled && handleUsBasedToggle(row), sx: {
                        p: '2px',
                        color: '#D0D5DD',
                        cursor: isUsBasedDisabled ? 'default' : 'pointer',
                        '&.Mui-checked': { color: '#EE2A7B' },
                        '&.Mui-disabled': { color: currentUsBased ? '#EE2A7B' : '#D0D5DD' },
                    } }));
            }
            case 'gender':
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#475467' }, children: row.gender || '—' }));
            case 'status': {
                const sty = statusStyles[row.status] || statusStyles.Pending;
                return (_jsx(Chip, { label: sty.label, size: "small", sx: {
                        height: 22,
                        borderRadius: '999px',
                        bgcolor: sty.bg,
                        color: sty.color,
                        fontSize: '12px',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: '8px' },
                    } }));
            }
            case 'action':
                return (_jsxs(Box, { sx: { position: 'relative', display: 'inline-flex', cursor: 'pointer' }, onClick: () => handleOpenNotes(row), children: [_jsx(IconButton, { size: "small", sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: "Notes", children: _jsx("img", { src: "/assets/book.svg", alt: "Note", style: { width: 18, height: 18 } }) }), row.notes && (_jsx(Box, { sx: {
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                bgcolor: '#F04438',
                                border: '1px solid #fff',
                            } }))] }));
            default:
                return null;
        }
    };
    const renderEmptyState = () => (_jsx(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
        }, children: _jsx(Box, { component: "img", src: "/assets/outreach-tracker-table.svg", alt: "No data", sx: { maxWidth: '200px', width: '100%', mb: 2 } }) }));
    const statusOptions = [
        { value: 'All', label: 'All' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Approved', label: 'Approved' },
        { value: 'Rejected', label: 'Rejected' },
    ];
    // ── IDs for AssignToListModal ─────────────────────────────────────────────
    const assignContactIds = selectedRowIds.size > 0
        ? Array.from(selectedRowIds).filter(id => {
            const c = currentListDetails.find(r => r.id === id);
            return isApproved(c) && !isIneligible(c) && !isBounced(c) && !!c?.primary_email?.trim() && !!c?.hubspot_sync_date;
        })
        : currentListDetails
            .filter(c => isApproved(c) && !isIneligible(c) && !isBounced(c) && !!c?.primary_email?.trim() && !!c?.hubspot_sync_date)
            .map(c => c.id);
    // ── Tracker type label for UI ─────────────────────────────────────────────
    const trackerTypeLabel = trackerType === 'youtube' ? 'YouTube' : 'Social';
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(PagesCommonHeader, { title: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: '14px', fontWeight: 600, color: '#667085', cursor: 'pointer' }, onClick: handleBack, children: displayClientName || 'Back' }), _jsx(Typography, { sx: { fontSize: '14px', color: '#D0D5DD' }, children: "\u203A" }), editingTrackerName && !isClient ? (_jsx(TextField, { value: tempTrackerName, onChange: e => setTempTrackerName(e.target.value), onKeyDown: e => {
                                        if (e.key === 'Enter')
                                            handleSaveTrackerName();
                                        if (e.key === 'Escape')
                                            handleCancelTrackerName();
                                    }, autoFocus: true, size: "small", disabled: savingTrackerName, sx: {
                                        '& .MuiOutlinedInput-root': {
                                            height: '28px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#101828',
                                            '& fieldset': { borderColor: '#EE2A7B', borderRadius: '6px' },
                                            '&:hover fieldset': { borderColor: '#EE2A7B' },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#EE2A7B',
                                                boxShadow: '0px 0px 0px 3px rgba(238,42,123,0.1)',
                                            },
                                        },
                                        minWidth: '260px',
                                    } })) : (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75 }, children: [_jsx(Typography, { onClick: () => {
                                                if ((isClient && !hasManageTrackersAccess) || viewOnly)
                                                    return;
                                                setTempTrackerName(trackerListName);
                                                setEditingTrackerName(true);
                                            }, sx: {
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: '#101828',
                                                cursor: (isClient && !hasManageTrackersAccess) || viewOnly ? 'default' : 'pointer',
                                                borderBottom: '1px dashed transparent',
                                                ...(!((isClient && !hasManageTrackersAccess) || viewOnly) && {
                                                    '&:hover': {
                                                        borderBottomColor: '#EE2A7B',
                                                        color: '#EE2A7B',
                                                    },
                                                }),
                                            }, children: trackerListName || `${trackerTypeLabel} Tracker` }), !((isClient && !hasManageTrackersAccess) || viewOnly) && (_jsx(HoverTooltip, { title: "Edit tracker name", placement: "top", children: _jsx(IconButton, { size: "small", onClick: () => {
                                                    setTempTrackerName(trackerListName);
                                                    setEditingTrackerName(true);
                                                }, sx: { p: '2px', color: '#98A2B3', '&:hover': { color: '#EE2A7B' } }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }), _jsx("path", { d: "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" })] }) }) }))] }))] }), rightSection: _jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: !viewOnly && hasManageTrackersAccess && (_jsxs(_Fragment, { children: [_jsx(IconButton, { onClick: e => setHubspotAnchor(e.currentTarget), sx: {
                                            width: 36,
                                            height: 36,
                                            borderRadius: '8px',
                                            border: '1px solid #D0D5DD',
                                            p: '6px',
                                            '&:hover': { bgcolor: '#F9FAFB' },
                                        }, children: _jsx(Box, { component: "img", src: "/assets/hubspot.svg", alt: "HubSpot", sx: { width: 20, height: 20 } }) }), _jsxs(Menu, { anchorEl: hubspotAnchor, open: Boolean(hubspotAnchor), onClose: () => setHubspotAnchor(null), anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, sx: {
                                            zIndex: 1500,
                                            '& .MuiPaper-root': {
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 16px rgba(16,24,40,0.12)',
                                                border: '1px solid #EAECF0',
                                                minWidth: '200px',
                                                mt: 0.5,
                                            },
                                        }, children: [_jsxs(MenuItem, { onClick: handleContactSync, disabled: savingPending, sx: { gap: 1.5, py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(RefreshCw, { size: 16 }), "Contact sync"] }), _jsxs(MenuItem, { onClick: () => {
                                                    setHubspotAnchor(null);
                                                    setAssignToListOpen(true);
                                                }, sx: { gap: 1.5, py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(UserCheck, { size: 16 }), "Contacts Assign To List"] })] }), Boolean(hubspotAnchor) && (_jsx(Portal, { children: _jsx(Box, { onClick: () => setHubspotAnchor(null), sx: {
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                width: '100vw',
                                                height: '100vh',
                                                bgcolor: 'rgba(0,0,0,0.15)',
                                                zIndex: 1400,
                                            } }) }))] })) }) }), !isClient && (_jsx(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#fff',
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            p: 1.5,
                        }, children: _jsx(LastUpdatedInfo, { updatedAt: latestHubspotSyncDate, onRefresh: handleContactSync, loading: loading || savingPending, showIcon: true }) })), (!isClient || hasManageTrackersAccess) && (_jsxs(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: 2,
                            width: '100%',
                        }, children: [_jsx(MetricCard, { title: "Total Contacts", value: totalContactsCount, icon: _jsx(BookOpen, { size: 16 }), tooltipText: "Total number of contacts in this tracker.", sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "Bounced Contacts", value: bouncedContacts.length, icon: _jsx(TrendingDown, { size: 16 }), tooltipText: "Contacts that have been synced to HubSpot and bounced in a sequence.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(bouncedContacts, 'bounced-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "Enrolled/Ineligible", value: ineligibleContacts.length, icon: _jsx(Inbox, { size: 16 }), tooltipText: "Contacts that have been synced to HubSpot and enrolled in a sequence.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(ineligibleContacts, 'enrolled-ineligible-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "Unenrolled/Eligible", value: eligibleContacts.length, icon: _jsx(Radio, { size: 16 }), tooltipText: "Contacts synced to HubSpot but not enrolled in any sequence.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(eligibleContacts, 'unenrolled-eligible-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "New/Eligible Contacts", value: newContacts.length, icon: _jsx(User, { size: 16 }), tooltipText: "Contacts that have not yet been synced to HubSpot.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(newContacts, 'new-eligible-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } })] })), _jsxs(Box, { sx: {
                            bgcolor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #EAECF0',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 2,
                                    borderBottom: '1px solid #EAECF0',
                                    flexWrap: 'nowrap',
                                    gap: 2,
                                }, children: [_jsxs(Typography, { sx: { fontSize: '16px', fontWeight: 700, color: '#101828' }, children: [trackerTypeLabel, " Tracker List"] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }, children: [_jsx(Dropdown, { value: selectedStatusFilter, onChange: v => {
                                                    setSelectedStatusFilter(v);
                                                    setPage(1);
                                                }, options: statusOptions, displayValue: _jsxs("span", { className: "all-Font-Sizes", children: [_jsxs("span", { style: { color: '#101828', fontWeight: 500 }, children: ["Status :", ' '] }), _jsx("span", { style: { color: '#667085' }, children: selectedStatusFilter })] }), size: "sm", fullWidth: false, className: "header-dropdown" }), !isClient && !viewOnly && hasClientApprovalAccess && (_jsx(Button, { variant: "outlined", startIcon: sendingApproval ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : _jsx(Send, { size: 16 }), onClick: handleSendForApproval, disabled: sendingApproval || !trackerId, sx: {
                                                    height: '36px',
                                                    borderColor: '#D0D5DD',
                                                    color: '#344054',
                                                    backgroundColor: '#fff',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '13px',
                                                    borderRadius: '8px',
                                                    '&:hover': { borderColor: '#D0D5DD', bgcolor: '#F9FAFB' },
                                                    '&.Mui-disabled': { borderColor: '#EAECF0', color: '#98A2B3', backgroundColor: '#F9FAFB' }
                                                }, children: sendingApproval ? 'Sending...' : 'Send For Approval' })), _jsx(ExportButton, { onClick: () => exportContactsToXLSX(filteredRows, `${trackerTypeLabel.toLowerCase()}-tracker-contacts`) })] })] }), _jsx(Box, { sx: { position: 'relative' }, children: _jsx(CommonTable, { columns: columns, rows: paginatedRows, renderCell: renderCell, isLoading: loading, isFirstLoad: loading && paginatedRows.length === 0, renderEmpty: renderEmptyState, enableSelection: !(isClient || viewOnly), selectedRows: selectedRowIds, onSelectionChange: setSelectedRowIds, stickyHeader: true, maxHeight: "calc(100vh - 420px)", expandTrigger: "none" }) }), totalEntries > 0 && (_jsx(Box, { sx: { p: '12px 16px', borderTop: '1px solid #EAECF0' }, children: _jsx(Pagination, { currentPage: page, totalPages: totalPages, totalEntries: totalEntries, itemsPerPage: itemsPerPage, onPageChange: setPage }) }))] })] }), _jsx(NotesModal, { open: notesOpen, onClose: () => setNotesOpen(false), initialNotes: notesValue, onSave: handleSaveNotes, actionLoading: notesSaving }), _jsx(ConfirmActionModal, { open: deleteModalOpen, onClose: () => setDeleteModalOpen(false), onConfirm: handleBulkDelete, title: `Delete ${trackerTypeLabel} Tracker Contacts`, description: `Are you sure you want to delete the ${selectedRowIds.size} selected contact${selectedRowIds.size === 1 ? '' : 's'}? This action is permanent and cannot be undone.`, confirmText: "Delete", cancelText: "Cancel", loading: savingPending }), _jsx(AssignToListModal, { open: assignToListOpen, onClose: () => setAssignToListOpen(false), trackerId: trackerId || '', selectedContactIds: assignContactIds, onSuccess: async () => {
                    setSavingPending(true);
                    try {
                        await refreshTrackerData();
                    }
                    catch { }
                    setSavingPending(false);
                }, apiBase: "/api/social-yt-tracker" }), _jsx(FloatingSaveBar, { isVisible: editingTrackerName && !(isClient || viewOnly), count: 1, message: "Unsaved tracker name change", countLabel: "Unsaved Change", onReset: handleCancelTrackerName, onSave: handleSaveTrackerName, updating: savingTrackerName, saveLabel: "Save Name" }), _jsx(FloatingSaveBar, { isVisible: !editingTrackerName && pendingEditRowsCount > 0, count: pendingEditRowsCount, message: "Unsaved row edits pending", countLabel: "Rows", onReset: () => setPendingEdits({}), onSave: handleSaveAllPendingEdits, updating: savingPending, saveLabel: "Save Changes" }), _jsx(FloatingSaveBar, { isVisible: !editingTrackerName && selectedRowIds.size > 0 && pendingEditRowsCount === 0, count: selectedRowIds.size, message: `${trackerTypeLabel} contacts selected for bulk action`, countLabel: "Selected Rows", onReset: () => {
                    setSelectedRowIds(new Set());
                    setStagedStatus('');
                }, children: _jsxs(Box, { sx: { display: 'flex', gap: 1.5, alignItems: 'center' }, children: [(hasManageTrackersAccess || hasClientApprovalAccess) && (_jsx(Dropdown, { usePortal: true, value: stagedStatus, onChange: v => setStagedStatus(v), options: [
                                { value: 'Pending', label: 'Pending' },
                                { value: 'Approved', label: 'Approved' },
                                { value: 'Rejected', label: 'Rejected' },
                            ], displayValue: _jsx("span", { className: "all-Font-Sizes", style: { color: '#475467', fontWeight: 600 }, children: stagedStatus || 'Change Status' }), size: "sm", fullWidth: false, className: "bulk-action-dropdown" })), stagedStatus && (hasManageTrackersAccess || hasClientApprovalAccess) && (_jsx(Button, { onClick: () => {
                                handleBulkStatusChange(stagedStatus);
                                setStagedStatus('');
                            }, disabled: savingPending, sx: {
                                textTransform: 'none',
                                bgcolor: '#EE2A7B',
                                color: '#fff',
                                fontWeight: 600,
                                px: 2,
                                height: '36px',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#D91F6A' },
                                '&.Mui-disabled': { bgcolor: '#f9a8d4', color: '#fff' },
                            }, children: savingPending ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : 'Save' })), !isClient && hasManageTrackersAccess && (_jsx(Button, { onClick: handleBulkHubspotSync, disabled: savingPending, startIcon: _jsx(Box, { component: "img", src: "/assets/hubspot.svg", alt: "", sx: { width: 16, height: 16 } }), sx: {
                                textTransform: 'none',
                                bgcolor: '#fff',
                                color: '#344054',
                                border: '1px solid #D0D5DD',
                                fontWeight: 600,
                                px: 2,
                                height: '36px',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#f9fafb' },
                                '&.Mui-disabled': { bgcolor: '#f2f4f7', color: '#98a2b3' },
                            }, children: "Sync HubSpot" })), !isClient && hasManageTrackersAccess && (_jsx(Button, { onClick: () => setDeleteModalOpen(true), disabled: savingPending, sx: {
                                textTransform: 'none',
                                bgcolor: '#D92D20',
                                color: '#fff',
                                fontWeight: 600,
                                px: 2,
                                height: '36px',
                                borderRadius: '8px',
                                boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
                                '&:hover': { bgcolor: '#B42318' },
                                '&.Mui-disabled': { bgcolor: '#fca5a5', color: '#fff' },
                            }, children: "Delete" }))] }) })] }));
}
